import { IcoClose16 } from '@onefootprint/icons';
import type { OnboardingDataEntry } from '@onefootprint/request-types/dashboard';
import { CaptureEscKeydown, IconButton, MonacoProvider, type OnEscKeydownHandler } from '@onefootprint/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OnboardingDataEntryViewer, { type NormalizedArtifact } from '@/components/onboarding-data-entry-viewer';
import Sidebar from '@/components/sidebar';
import usePermissions from '@/hooks/use-permissions';
type ArtifactsViewerProps = {
  open: boolean;
  onClose: () => void;
  artifacts: OnboardingDataEntry[];
};
const ArtifactsViewer = ({
  open,
  onClose,
  artifacts
}: ArtifactsViewerProps) => {
  const {
    t
  } = useTranslation('entity-details', {
    keyPrefix: 'onboardingTraceDialog.artifacts'
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const {
    hasPermission
  } = usePermissions();
  const canManuallyReview = hasPermission('manual_review');
  useEffect(() => {
    if (!containerRef.current) return;
    if (height === null) {
      setHeight(containerRef.current.clientHeight - 64);
    }
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setHeight(entry.contentRect.height - 64);
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerRef.current]);
  const normalizedArtifacts: NormalizedArtifact[] = artifacts.map(entry_0 => ({
    ...entry_0,
    artifactType: entry_0.schema?.kind === 'file' ? 'file' : 'data'
  }));
  const onEscKeydown: OnEscKeydownHandler = {
    handler: onClose,
    deps: []
  };
  return <MonacoProvider languageName="template">
      <Sidebar isOpen={open} title={t($ => $.title)} size="xl" onClickOutside={onClose}>
        <div className="flex flex-col h-full">
          <CaptureEscKeydown id="artifacts-viewer" onEscKeydown={onEscKeydown} />
          <div className="flex items-center justify-between px-4 py-3 border-b border-tertiary shrink-0">
            <h3 className="text-label-2 text-primary">{t($_0 => $_0.title)}</h3>
            <IconButton icon={IcoClose16} aria-label="Close" onClick={onClose} />
          </div>
          <div className="flex-1 min-h-0 p-4" ref={containerRef}>
            {canManuallyReview ? <OnboardingDataEntryViewer artifacts={normalizedArtifacts} height={height ?? 0} /> : <div className="flex items-center justify-center py-8">
                <p className="text-body-2 text-error text-center">{t($_1 => $_1.errorLoading)}</p>
              </div>}
          </div>
        </div>
      </Sidebar>
    </MonacoProvider>;
};
export default ArtifactsViewer;